import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../api/apiClient';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { RefreshCw, Plus, Trash2, PlayCircle } from 'lucide-react';

export function AdminScraperPage() {
    const [newUrl, setNewUrl] = useState('');
    const queryClient = useQueryClient();

    // Fetch all scraper jobs
    const { data: jobsData, refetch } = useQuery({
        queryKey: ['scraper-jobs'],
        queryFn: () => apiClient.admin.getScraperJobs(),
        refetchInterval: 3000 // Auto-refresh every 3 seconds
    });

    // Fetch queue stats
    const { data: stats } = useQuery({
        queryKey: ['scraper-stats'],
        queryFn: () => apiClient.admin.getScraperStats(),
        refetchInterval: 2000
    });

    // Add new scraping job
    const addJobMutation = useMutation({
        mutationFn: (brandUrl) => apiClient.admin.addScraperJob(brandUrl),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['scraper-jobs'] });
            setNewUrl('');
        }
    });

    const handleAddJob = (e) => {
        e.preventDefault();
        if (newUrl.trim()) {
            addJobMutation.mutate(newUrl);
        }
    };

    // Fetch settings
    const { data: settingsData, refetch: refetchSettings } = useQuery({
        queryKey: ['admin-settings'],
        queryFn: () => apiClient.admin.getSettings()
    });

    const [scheduleTime, setScheduleTime] = useState('');

    const updateScheduleMutation = useMutation({
        mutationFn: (newSchedule) => apiClient.admin.updateSettings('scraper_schedule', newSchedule),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin-settings'] });
            setScheduleTime('');
        }
    });

    const handleAddSchedule = (e) => {
        e.preventDefault();
        if (!scheduleTime) return;

        const currentSchedule = settingsData?.scraper_schedule || [];
        if (!currentSchedule.includes(scheduleTime)) {
            const newSchedule = [...currentSchedule, scheduleTime].sort();
            updateScheduleMutation.mutate(newSchedule);
        }
    };

    const handleRemoveSchedule = (timeToRemove) => {
        const currentSchedule = settingsData?.scraper_schedule || [];
        const newSchedule = currentSchedule.filter(time => time !== timeToRemove);
        updateScheduleMutation.mutate(newSchedule);
    };

    const getStatusBadge = (status) => {
        const colors = {
            pending: 'bg-yellow-500',
            running: 'bg-amber-500',
            completed: 'bg-green-500',
            failed: 'bg-red-500'
        };
        return <Badge className={colors[status] || 'bg-gray-500'}>{status}</Badge>;
    };

    const formatDate = (date) => {
        return new Date(date).toLocaleString('fr-FR');
    };

    return (
        <div className="container mx-auto p-6 space-y-6">
            <div className="flex items-center justify-between">
                <h1 className="text-3xl font-bold">Admin - Scraper</h1>
                <Button onClick={() => { refetch(); refetchSettings(); }} variant="outline" size="sm">
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Rafraîchir
                </Button>
            </div>

            {/* Stats */}
            {stats && (
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                    <Card>
                        <CardHeader className="pb-2">
                            <CardDescription>Total</CardDescription>
                            <CardTitle>{stats.total}</CardTitle>
                        </CardHeader>
                    </Card>
                    <Card>
                        <CardHeader className="pb-2">
                            <CardDescription>En attente</CardDescription>
                            <CardTitle>{stats.pending}</CardTitle>
                        </CardHeader>
                    </Card>
                    <Card>
                        <CardHeader className="pb-2">
                            <CardDescription>En cours</CardDescription>
                            <CardTitle>{stats.running}</CardTitle>
                        </CardHeader>
                    </Card>
                    <Card>
                        <CardHeader className="pb-2">
                            <CardDescription>Complétés</CardDescription>
                            <CardTitle>{stats.completed}</CardTitle>
                        </CardHeader>
                    </Card>
                    <Card>
                        <CardHeader className="pb-2">
                            <CardDescription>Échoués</CardDescription>
                            <CardTitle>{stats.failed}</CardTitle>
                        </CardHeader>
                    </Card>
                </div>
            )}

            {/* Scheduler Settings */}
            <Card>
                <CardHeader>
                    <CardTitle>Planning de Scraping Automatique</CardTitle>
                    <CardDescription>
                        Définissez les heures auxquelles tous les sites seront scrapés automatiquement chaque jour.
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="mb-4">
                        <h4 className="text-sm font-medium mb-2">Horaires programmés :</h4>
                        <div className="flex flex-wrap gap-2">
                            {settingsData?.scraper_schedule?.length > 0 ? (
                                settingsData.scraper_schedule.map((time) => (
                                    <Badge key={time} variant="secondary" className="px-3 py-1 text-sm flex items-center gap-2">
                                        {time}
                                        <button
                                            onClick={() => handleRemoveSchedule(time)}
                                            className="text-muted-foreground hover:text-destructive transition-colors"
                                            disabled={updateScheduleMutation.isPending}
                                        >
                                            <Trash2 className="w-3 h-3" />
                                        </button>
                                    </Badge>
                                ))
                            ) : (
                                <p className="text-sm text-muted-foreground italic">Aucun horaire défini.</p>
                            )}
                        </div>
                    </div>

                    <form onSubmit={handleAddSchedule} className="flex gap-2 items-end max-w-xs">
                        <div className="grid w-full items-center gap-1.5">
                            <label htmlFor="time" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                                Ajouter un horaire
                            </label>
                            <Input
                                type="time"
                                id="time"
                                value={scheduleTime}
                                onChange={(e) => setScheduleTime(e.target.value)}
                                required
                            />
                        </div>
                        <Button type="submit" disabled={!scheduleTime || updateScheduleMutation.isPending}>
                            <Plus className="w-4 h-4 mr-2" />
                            Ajouter
                        </Button>
                    </form>
                </CardContent>
            </Card>

            {/* Add new job */}
            <Card>
                <CardHeader>
                    <CardTitle>Lancement Manuel</CardTitle>
                    <CardDescription>
                        Ajouter un site spécifique à scraper immédiatement
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleAddJob} className="flex gap-2">
                        <Input
                            type="url"
                            placeholder="https://marque-independante.com/collections/all"
                            value={newUrl}
                            onChange={(e) => setNewUrl(e.target.value)}
                            className="flex-1"
                        />
                        <Button type="submit" disabled={addJobMutation.isPending}>
                            <Plus className="w-4 h-4 mr-2" />
                            Scraper ce site
                        </Button>
                    </form>
                </CardContent>
            </Card>

            {/* Jobs list */}
            <Card>
                <CardHeader>
                    <CardTitle>File d'attente</CardTitle>
                    <CardDescription>État des scrapeurs en temps réel</CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="space-y-4">
                        {jobsData?.jobs?.length === 0 && (
                            <p className="text-center text-muted-foreground py-8">
                                Aucun job en cours.
                            </p>
                        )}
                        {jobsData?.jobs?.map((job) => (
                            <div key={job.id} className="border rounded-lg p-4">
                                <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-2">
                                            {getStatusBadge(job.status)}
                                            <span className="text-sm text-muted-foreground">
                                                #{job.id}
                                            </span>
                                        </div>
                                        <p className="font-medium mb-1 break-all">{job.brandUrl}</p>
                                        <div className="text-sm text-muted-foreground space-y-1">
                                            <p>Créé: {formatDate(job.createdAt)}</p>
                                            {job.startedAt && <p>Démarré: {formatDate(job.startedAt)}</p>}
                                            {job.completedAt && <p>Terminé: {formatDate(job.completedAt)}</p>}
                                            {job.status === 'completed' && (
                                                <p className="text-green-600 font-medium">
                                                    {job.productsFound} produits trouvés
                                                </p>
                                            )}
                                            {job.error && (
                                                <p className="text-red-600">Erreur: {job.error}</p>
                                            )}
                                        </div>
                                    </div>
                                    {job.status === 'running' && (
                                        <div className="ml-4 flex flex-col items-center">
                                            <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                                                <div
                                                    className="h-full bg-amber-500 transition-all duration-300"
                                                    style={{ width: `${job.progress}%` }}
                                                />
                                            </div>
                                            <p className="text-xs text-center mt-1">{job.progress}%</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
